import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sprout,
  LayoutDashboard,
  Users,
  Wheat,
  Package,
  GraduationCap,
  Truck,
  ShoppingBag,
  Map,
  FileBarChart2,
  LogOut,
  ChevronLeft,
  ChevronRight,
  User,
  Shield,
  Bot,
  Home,
  Bell,
  Search,
  Menu,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';

  // Navigation Structure
  const menuGroups = [
    {
      title: 'PANEL DE CONTROL',
      items: isAdmin ? [
        { path: '/admin', label: 'Panel General', icon: LayoutDashboard },
        { path: '/executive-dashboard', label: 'Panel Ejecutivo', icon: FileBarChart2 }
      ] : [
        { path: '/producer', label: 'Mi Panel Productor', icon: LayoutDashboard },
        { path: '/producer/profile', label: 'Mi Perfil', icon: User }
      ]
    },
    {
      title: 'GESTIÓN AGRÍCOLA',
      items: isAdmin ? [
        { path: '/producers', label: 'Productores', icon: Users },
        { path: '/crops', label: 'Gestión Cultivos', icon: Wheat },
        { path: '/supplies', label: 'Insumos Agrícolas', icon: Package },
        { path: '/training', label: 'Capacitaciones', icon: GraduationCap }
      ] : [
        { path: '/producer/crops', label: 'Mis Cultivos', icon: Wheat },
        { path: '/supplies', label: 'Insumos Disponibles', icon: Package },
        { path: '/producer/trainings', label: 'Capacitaciones', icon: GraduationCap }
      ]
    },
    {
      title: 'OPERACIONES',
      items: isAdmin ? [
        { path: '/logistics', label: 'Logística & Rutas', icon: Truck },
        { path: '/commerce', label: 'Comercio Comunal', icon: ShoppingBag }
      ] : [
        { path: '/producer/logistics', label: 'Mi Logística', icon: Truck },
        { path: '/commerce', label: 'Comercio Comunal', icon: ShoppingBag }
      ]
    },
    {
      title: 'ANÁLISIS Y MAPAS',
      items: [
        { path: '/map', label: 'Geolocalización', icon: Map },
        { path: '/reports', label: 'Reportes & IA', icon: Bot }
      ]
    }
  ];

  const sidebarContent = (
    <div className="flex flex-col h-full bg-slate-900 text-gray-200 border-r border-slate-800 selection:bg-yellow-400 selection:text-slate-900">
      {/* Brand Header */}
      <div className="p-4 flex items-center justify-between border-b border-slate-800">
        <Link to="/" className="flex items-center space-x-3 overflow-hidden">
          <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-emerald-700 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-950">
            <Sprout className="w-6 h-6 text-yellow-300" />
          </div>
          {!collapsed && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col"
            >
              <span className="font-extrabold text-lg text-white tracking-tight leading-none flex items-center">
                SISCIAC
                <span className="ml-1.5 w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              </span>
              <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mt-1">
                {isAdmin ? 'Módulo Administrativo' : 'Panel del Productor'}
              </span>
            </motion.div>
          )}
        </Link>

        {/* Collapse button for desktop */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          title={collapsed ? "Expandir menú" : "Colapsar menú"}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-6 custom-scrollbar">
        {menuGroups.map((group, idx) => (
          <div key={idx} className="space-y-1">
            {!collapsed && (
              <h3 className="px-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                {group.title}
              </h3>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen && setMobileOpen(false)}
                  className={`flex items-center px-3 py-2.5 rounded-xl font-medium text-sm transition-all duration-150 group relative ${
                    isActive
                      ? 'bg-gradient-to-r from-green-600 to-emerald-700 text-white font-semibold shadow-md shadow-green-950/50'
                      : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon
                    className={`w-5 h-5 flex-shrink-0 transition-transform group-hover:scale-110 ${
                      isActive ? 'text-yellow-300' : 'text-slate-400 group-hover:text-yellow-400'
                    } ${collapsed ? 'mx-auto' : 'mr-3'}`}
                  />
                  {!collapsed && (
                    <span className="truncate">{item.label}</span>
                  )}
                  {isActive && !collapsed && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-yellow-400 shadow-sm shadow-yellow-400" />
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      {/* Footer Profile & Landing link */}
      <div className="p-3 border-t border-slate-800 bg-slate-950/60 space-y-2">
        <Link
          to="/"
          className="flex items-center justify-center space-x-2 w-full py-2 px-3 rounded-lg bg-slate-800/70 hover:bg-slate-800 text-slate-300 hover:text-yellow-300 text-xs font-semibold transition-colors"
        >
          <Home className="w-4 h-4 text-yellow-400" />
          {!collapsed && <span>Página Principal (Landing)</span>}
        </Link>

        <div className="flex items-center justify-between p-2 rounded-xl bg-slate-900 border border-slate-800">
          <div className="flex items-center space-x-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 flex items-center justify-center font-bold text-sm flex-shrink-0">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-white truncate">{user?.name || 'Usuario'}</p>
                <p className="text-[10px] text-slate-400 uppercase font-semibold flex items-center">
                  <Shield className="w-2.5 h-2.5 text-green-400 mr-1" />
                  {isAdmin ? 'Administrador' : 'Productor'}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Cerrar Sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col h-screen sticky top-0 z-40 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}>
        {sidebarContent}
      </aside>

      {/* Mobile Drawer Backdrop & Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50"
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 shadow-2xl"
            >
              <div className="absolute top-3 right-3 z-10">
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
